import React, { useRef, useState } from "react";
import { ThreeDotsIcon, DeleteIcon } from "../../../assets";
import CustomTooltip from "../../Tooltip/CustomTooltip";
import AnchoredPanel from "../../shared/AnchoredPanel";

export const DropdownOptions = ({ handleDeleteClick }) => {
  return (
    <div
      role="menu"
      className="px-2 py-1.5"
    >
      <button
        type="button"
        role="menuitem"
        aria-label="Delete"
        onClick={handleDeleteClick}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-[12px] text-ink-red-600 dark:text-ink-red-400 hover:bg-fill-red-50 dark:hover:bg-fill-red-900/20 rounded-lg transition-all duration-200 cursor-pointer"
      >
        <DeleteIcon className="w-3.5 h-3.5 fill-ink-red-600 dark:fill-ink-red-400" />
        <span className="font-medium">Delete</span>
      </button>
    </div>
  );
};

const CascadeDeleteConfirmation = ({ onCancel, onConfirm }) => (
  <div role="alertdialog" aria-labelledby="cascade-delete-title" className="p-3">
    <p id="cascade-delete-title" className="text-sm font-semibold text-ink-ink dark:text-ink-white">
      Delete this turn and all responses after it?
    </p>
    <p className="mt-1 text-xs leading-5 text-ink-gray-500 dark:text-ink-gray-400">
      Later turns depend on this conversation context and will also be deleted. This cannot be undone.
    </p>
    <div className="mt-3 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-1.5 text-xs font-medium text-ink-gray-500 dark:text-ink-gray-400 border border-edge-gray-200 dark:border-edge-panel-border rounded-md hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        className="px-3 py-1.5 text-xs font-medium text-ink-on-color bg-fill-red-600 rounded-md hover:bg-fill-red-700"
      >
        Delete all
      </button>
    </div>
  </div>
);

const MessagesActionDropdown = ({
  prompt_id,
  deleteMessage,
  answer_id,
  isLatest = true,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const dropdownRef = useRef(null);
  const toggleAttachDropdown = () => {
    setConfirmOpen(false);
    setOpen((prev) => !prev);
  };

  const handleDeleteClick = () => {
    if (isLatest) {
      deleteMessage(prompt_id, answer_id, false);
      setOpen(false);
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteMessage(prompt_id, answer_id, true);
    setOpen(false);
    setConfirmOpen(false);
  };

  const closePanel = () => {
    setOpen(false);
    setConfirmOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <CustomTooltip content="Message options">
        <button
          type="button"
          onClick={toggleAttachDropdown}
          aria-label="Message options"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          className="p-2 rounded-full hover:bg-fill-icon-hover/25 dark:hover:bg-fill-icon-hover-dark/100 transition-all duration-200 group"
        >
          <ThreeDotsIcon className="w-4 h-4 fill-ink-ink dark:fill-ink-white group-hover:fill-ink-gray-700 dark:group-hover:fill-ink-gray-200 transition-colors duration-200" />
        </button>
      </CustomTooltip>

      <AnchoredPanel
        anchorRef={dropdownRef}
        isOpen={isOpen}
        onClose={closePanel}
        width={isConfirmOpen ? 340 : 120}
      >
        {isConfirmOpen ? (
          <CascadeDeleteConfirmation
            onCancel={closePanel}
            onConfirm={handleConfirmDelete}
          />
        ) : (
          <DropdownOptions handleDeleteClick={handleDeleteClick} />
        )}
      </AnchoredPanel>
    </div>
  );
};

export default MessagesActionDropdown;
